import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const globalForSupabase = global as unknown as {
  supabaseClient: SupabaseClient | undefined;
};

/**
 * Initialize Supabase client with service role key
 * Uses environment variables for configuration
 */
function createSupabaseClient(): SupabaseClient {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseServiceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;

  if (!supabaseUrl || !supabaseServiceKey) {
    throw new Error(
      'Missing Supabase configuration. Ensure NEXT_PUBLIC_SUPABASE_URL and SUPABASE_SERVICE_ROLE_KEY are set.'
    );
  }

  return createClient(supabaseUrl, supabaseServiceKey, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  });
}

/**
 * Get or create Supabase client (singleton pattern)
 */
export function getSupabaseClient(): SupabaseClient {
  if (!globalForSupabase.supabaseClient) {
    globalForSupabase.supabaseClient = createSupabaseClient();
  }
  return globalForSupabase.supabaseClient;
}

/**
 * Type-safe wrapper for Supabase queries
 * Provides consistent error handling and type safety
 */
export async function executeQuery<T>(
  query: Promise<{ data: T | null; error: any }>
): Promise<T> {
  const { data, error } = await query;

  if (error) {
    throw new SupabaseQueryError(
      `Supabase query failed: ${error.message}`,
      error.code || 'UNKNOWN',
      error
    );
  }

  if (data === null) {
    throw new SupabaseQueryError('Query returned no data', 'NO_DATA');
  }

  return data;
}

/**
 * Type-safe wrapper for Supabase queries that may return null
 */
export async function executeQueryNullable<T>(
  query: Promise<{ data: T | null; error: any }>
): Promise<T | null> {
  const { data, error } = await query;

  if (error) {
    throw new SupabaseQueryError(
      `Supabase query failed: ${error.message}`,
      error.code || 'UNKNOWN',
      error
    );
  }

  return data;
}

/**
 * Type-safe wrapper for Supabase queries that return arrays
 */
export async function executeQueryArray<T>(
  query: Promise<{ data: T[] | null; error: any }>
): Promise<T[]> {
  const { data, error } = await query;

  if (error) {
    throw new SupabaseQueryError(
      `Supabase query failed: ${error.message}`,
      error.code || 'UNKNOWN',
      error
    );
  }

  return data || [];
}

/**
 * Custom error class for Supabase query errors
 */
export class SupabaseQueryError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'SupabaseQueryError';
  }
}

/**
 * Error class for item not found
 */
export class ItemNotFoundError extends SupabaseQueryError {
  constructor(message: string = 'Item not found') {
    super(message, 'ITEM_NOT_FOUND');
    this.name = 'ItemNotFoundError';
  }
}

/**
 * Error class for validation errors
 */
export class ValidationError extends SupabaseQueryError {
  constructor(message: string) {
    super(message, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}
